/**
 * Application shell: Microsoft 365 suite bar, icon rail, content area.
 *
 * The chrome follows the design prototype in the pacxa-fluent repo
 * (`apps/orgatlas/docs/prototype/nexora-365-hcm-v2.html`): a 48px near-black
 * suite bar across the top, a 64px icon rail down the left with the label under
 * each glyph, and the content inset behind both. That is the Microsoft 365
 * frame, which is the point — this ERP sits beside Outlook and Teams all day.
 *
 * It replaces a 260px sidebar copied from the *legacy portal*, which was the
 * wrong ancestor: the portal predates the prototype and looks nothing like it.
 *
 * The nav is rendered from the capability list the server returned, so a user
 * only sees modules they can actually open. There is no hardcoded module list in
 * the browser: the legacy shell had three (two copies of a module→client-ID map
 * plus an OPEN_MODULES array).
 *
 * Two colours here are literal rather than tokens: the suite bar is `#1B1A19`
 * and its dark twin `#0E0D0C` — the prototype's `--suite`, a near-black that is
 * deliberately *not* the app surface and has no Fluent neutral equivalent.
 * Everything else is a theme token.
 */
import { useMsal } from '@azure/msal-react';
import {
  Grid20Regular,
  QuestionCircle20Regular,
  Search20Regular,
  SignOut20Regular,
  WeatherMoon20Regular,
  WeatherSunny20Regular,
} from '@fluentui/react-icons';
import {
  Avatar,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tooltip,
  makeStyles,
  mergeClasses,
  moduleIcon,
  tokens,
  useColorScheme,
  useNexoraStyles,
} from '@pacxa/ui';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';

import { usePrincipal, type VisibleModule } from '../auth/PrincipalProvider.js';

/** The prototype's `--suite`. No Fluent neutral sits this far from the surface. */
const SUITE_LIGHT = '#1B1A19';
const SUITE_DARK = '#0E0D0C';

const SUITE_HEIGHT = '48px';
const RAIL_WIDTH = '64px';

/** The prototype's waffle tile colours, cycled across the module list. */
const WAFFLE_COLOURS = ['#0F6CBD', '#8331A7', '#B7472A', '#5B5FC7', '#0E700E', '#B05C00', '#038387'];

const useStyles = makeStyles({
  root: {
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
  },

  /* ── Suite bar ──────────────────────────────────────────────────────────── */
  suite: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: SUITE_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    zIndex: 60,
    color: '#FFFFFF',
  },
  suiteLight: { backgroundColor: SUITE_LIGHT },
  suiteDark: { backgroundColor: SUITE_DARK },
  waffle: {
    display: 'grid',
    placeItems: 'center',
    width: '36px',
    height: '36px',
    flexShrink: 0,
    borderRadius: tokens.borderRadiusSmall,
    borderTopStyle: 'none',
    borderRightStyle: 'none',
    borderBottomStyle: 'none',
    borderLeftStyle: 'none',
    backgroundColor: 'transparent',
    color: 'inherit',
    cursor: 'pointer',
    ':hover': { backgroundColor: 'rgba(255,255,255,.12)' },
  },
  brand: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: '0.95rem',
    letterSpacing: '0.01em',
    whiteSpace: 'nowrap',
    color: '#FFFFFF',
  },
  search: {
    flexGrow: 1,
    maxWidth: '560px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  searchInput: {
    width: '100%',
    backgroundColor: 'rgba(255,255,255,.10)',
    borderTopStyle: 'none',
    borderRightStyle: 'none',
    borderLeftStyle: 'none',
    borderBottomStyle: 'none',
    color: '#FFFFFF',
    '::after': { borderBottomColor: 'transparent' },
  },
  suiteRight: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    marginLeft: 'auto',
  },
  suiteButton: {
    display: 'grid',
    placeItems: 'center',
    width: '36px',
    height: '36px',
    borderRadius: tokens.borderRadiusSmall,
    borderTopStyle: 'none',
    borderRightStyle: 'none',
    borderBottomStyle: 'none',
    borderLeftStyle: 'none',
    backgroundColor: 'transparent',
    color: 'inherit',
    cursor: 'pointer',
    ':hover': { backgroundColor: 'rgba(255,255,255,.12)' },
  },
  me: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalSNudge,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalXS,
    paddingTop: '4px',
    paddingBottom: '4px',
    borderRadius: tokens.borderRadiusSmall,
    borderTopStyle: 'none',
    borderRightStyle: 'none',
    borderBottomStyle: 'none',
    borderLeftStyle: 'none',
    backgroundColor: 'transparent',
    color: 'inherit',
    cursor: 'pointer',
    ':hover': { backgroundColor: 'rgba(255,255,255,.12)' },
  },
  meText: {
    display: 'none',
    textAlign: 'left',
    lineHeight: 1.15,
    '@media (min-width: 720px)': { display: 'block' },
  },
  meName: {
    display: 'block',
    fontSize: '0.78rem',
    fontWeight: tokens.fontWeightSemibold,
    color: '#FFFFFF',
  },
  meMail: { display: 'block', fontSize: '0.68rem', color: 'rgba(255,255,255,.72)' },

  /* ── Icon rail ──────────────────────────────────────────────────────────── */
  rail: {
    position: 'fixed',
    top: SUITE_HEIGHT,
    left: 0,
    bottom: 0,
    width: RAIL_WIDTH,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: '1px',
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    // Scrollable, but without the scrollbar drawn. At 64px wide a Windows
    // scrollbar eats a quarter of the rail and sits on top of the labels, which
    // is what made it look broken. Keyboard and wheel scrolling still work; the
    // rail is short enough that it only overflows on very short viewports.
    overflowY: 'auto',
    scrollbarWidth: 'none',
    '::-webkit-scrollbar': { width: 0, height: 0 },
    zIndex: 50,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  railItem: {
    width: '58px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: '4px',
    paddingTop: '9px',
    paddingBottom: '7px',
    borderRadius: tokens.borderRadiusSmall,
    color: tokens.colorNeutralForeground2,
    fontSize: '0.6rem',
    lineHeight: 1.15,
    textAlign: 'center',
    textDecorationLine: 'none',
    ':hover': {
      color: tokens.colorBrandForeground1,
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  railItemActive: {
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
    backgroundColor: tokens.colorBrandBackground2,
    ':hover': {
      color: tokens.colorBrandForeground1,
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  railLabel: {
    display: 'block',
    maxWidth: '54px',
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  railSpacer: { flexGrow: 1, minHeight: tokens.spacingVerticalM },
  railButton: {
    borderTopStyle: 'none',
    borderRightStyle: 'none',
    borderBottomStyle: 'none',
    borderLeftStyle: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  /** The waffle flyout — the prototype's app grid, over the module list. */
  waffleList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 84px)',
    gap: '4px',
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
  },
  waffleApp: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: '6px',
    paddingTop: '10px',
    paddingBottom: '10px',
    borderRadius: tokens.borderRadiusMedium,
    fontSize: '0.68rem',
    lineHeight: 1.2,
    textAlign: 'center',
    textDecorationLine: 'none',
    color: tokens.colorNeutralForeground1,
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  waffleIcon: {
    display: 'grid',
    placeItems: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    color: '#FFFFFF',
  },
  waffleLabel: {
    maxWidth: '76px',
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  /* ── Content ────────────────────────────────────────────────────────────── */
  shell: {
    paddingTop: '64px',
    paddingLeft: '80px',
    paddingRight: tokens.spacingHorizontalL,
    paddingBottom: tokens.spacingVerticalXXL,
    maxWidth: '1760px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
});

/**
 * The rail is a flat list — it has no room for group headings — but the server's
 * category ordering still decides what sits next to what.
 */
function inCategoryOrder(modules: VisibleModule[]): VisibleModule[] {
  const byCategory = new Map<string, VisibleModule[]>();
  for (const mod of modules) {
    const key = mod.category ?? 'Other';
    const existing = byCategory.get(key);
    if (existing) existing.push(mod);
    else byCategory.set(key, [mod]);
  }
  return [...byCategory.values()].flat();
}

function initialsOf(name: string | undefined): string {
  return (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Module quick-switcher.
 *
 * The prototype puts a search field in the middle of the suite bar. There is no
 * global search behind this app, and a box that looks like search but does
 * nothing is worse than no box — so this one is scoped to what it can actually
 * do: filter the modules you are allowed to open, and navigate on Enter or
 * click.
 */
function ModuleSearch({ modules }: { modules: VisibleModule[] }) {
  const styles = useStyles();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return [];
    return modules.filter((m) => m.name.toLowerCase().includes(needle)).slice(0, 6);
  }, [query, modules]);

  const go = (route: string) => {
    setQuery('');
    void navigate(route);
  };

  return (
    <div className={styles.search}>
      <Menu open={matches.length > 0} positioning="below-start">
        <MenuTrigger disableButtonEnhancement>
          <Input
            value={query}
            placeholder="Find a module"
            appearance="filled-darker"
            className={styles.searchInput}
            contentBefore={<Search20Regular />}
            onChange={(_event, data) => setQuery(data.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && matches[0]) go(matches[0].route);
              if (event.key === 'Escape') setQuery('');
            }}
            aria-label="Find a module"
          />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            {matches.map((m) => (
              <MenuItem key={m.id} onClick={() => go(m.route)}>
                {m.name}
              </MenuItem>
            ))}
          </MenuList>
        </MenuPopover>
      </Menu>
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const styles = useStyles();
  const nx = useNexoraStyles();
  const location = useLocation();
  const { me } = usePrincipal();
  const { instance } = useMsal();
  const { colorScheme, setPreference } = useColorScheme();

  const modules = inCategoryOrder(me?.modules ?? []);
  const dark = colorScheme === 'dark';
  const nextScheme = dark ? 'light' : 'dark';

  return (
    <div className={styles.root}>
      <header
        className={mergeClasses(styles.suite, dark ? styles.suiteDark : styles.suiteLight)}
        role="banner"
      >
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <button type="button" className={styles.waffle} aria-label="All modules">
              <Grid20Regular />
            </button>
          </MenuTrigger>
          <MenuPopover>
            <div className={styles.waffleList}>
              {modules.map((mod, i) => {
                const Icon = moduleIcon(mod.icon);
                return (
                  <Link key={mod.id} to={mod.route} className={styles.waffleApp}>
                    <span
                      className={styles.waffleIcon}
                      style={{ backgroundColor: WAFFLE_COLOURS[i % WAFFLE_COLOURS.length] }}
                      aria-hidden
                    >
                      <Icon />
                    </span>
                    <span className={styles.waffleLabel}>{mod.name}</span>
                  </Link>
                );
              })}
            </div>
          </MenuPopover>
        </Menu>
        <span className={styles.brand}>Pacxa ERP</span>

        <ModuleSearch modules={modules} />

        <div className={styles.suiteRight}>
          <Tooltip content={`Switch to ${nextScheme} theme`} relationship="label">
            <button
              type="button"
              className={styles.suiteButton}
              aria-label={`Switch to ${nextScheme} theme`}
              onClick={() => setPreference(nextScheme)}
            >
              {dark ? <WeatherSunny20Regular /> : <WeatherMoon20Regular />}
            </button>
          </Tooltip>

          {/* The prototype's help button. It goes to the access screen, which is
              the only page here that answers "why can't I see X" — the question
              this button gets pressed for. Rendered only for people who can
              actually open that screen: a button that leads to a 403 is worse
              than no button, and there is no help centre to point at instead. */}
          {modules.some((mod) => mod.id === 'admin') ? (
            <Tooltip content="Who can do what, and why" relationship="label">
              <Link to="/admin" className={styles.suiteButton} aria-label="Access and permissions">
                <QuestionCircle20Regular />
              </Link>
            </Tooltip>
          ) : null}

          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <button type="button" className={styles.me} aria-label="Account">
                <Avatar
                  size={28}
                  color="colorful"
                  name={me?.user.displayName}
                  initials={initialsOf(me?.user.displayName)}
                />
                <span className={styles.meText}>
                  <span className={styles.meName}>{me?.user.displayName}</span>
                  <span className={styles.meMail}>{me?.user.email}</span>
                </span>
              </button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<SignOut20Regular />} onClick={() => void instance.logoutRedirect()}>
                  Sign out
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </header>

      <nav className={styles.rail} aria-label="Modules">
        {modules.map((mod) => {
          const Icon = moduleIcon(mod.icon);
          const active = location.pathname === mod.route;
          return (
            <Tooltip key={mod.id} content={mod.name} relationship="label" positioning="after">
              <NavLink
                to={mod.route}
                end={mod.route === '/'}
                className={({ isActive }) =>
                  mergeClasses(styles.railItem, isActive && styles.railItemActive)
                }
                aria-current={active ? 'page' : undefined}
              >
                <Icon filled={active} aria-hidden />
                <span className={styles.railLabel}>{mod.name}</span>
              </NavLink>
            </Tooltip>
          );
        })}

        {/* `.rail-sp` then the theme button, exactly as the prototype: the rail
            ends with a control rather than trailing off. */}
        <span className={styles.railSpacer} />
        <Tooltip content={`Switch to ${nextScheme} theme`} relationship="label" positioning="after">
          <button
            type="button"
            className={mergeClasses(styles.railItem, styles.railButton)}
            aria-label={`Switch to ${nextScheme} theme`}
            onClick={() => setPreference(nextScheme)}
          >
            {dark ? <WeatherSunny20Regular /> : <WeatherMoon20Regular />}
            <span className={styles.railLabel}>Theme</span>
          </button>
        </Tooltip>
      </nav>

      <main className={styles.shell}>
        {/* Keyed on the path so the entry animation replays when the user moves
            between modules. Routing already remounts the page component on a
            path change, so the key adds no extra unmounting. */}
        <div key={location.pathname} className={nx.fadeIn}>
          {children}
        </div>
      </main>
    </div>
  );
}
